<template>
  <div class="member-analysis">
    <el-card class="main-card glass-card">
      <template #header>
        <div class="card-header">
          <el-icon class="card-icon"><DataLine /></el-icon>
          <span>会员分析</span>
        </div>
      </template>
      <div class="card-content">
        <!-- 图表配置区域 -->
        <div class="chart-config">
          <el-row :gutter="20" type="flex" align="middle">
            <el-col :span="8">
              <el-select v-model="timeRange" placeholder="选择时间范围" style="width: 100%;" @change="fetchData">
                <el-option label="最近7天" value="7" />
                <el-option label="最近15天" value="15" />
                <el-option label="最近30天" value="30" />
                <el-option label="最近60天" value="60" />
              </el-select>
            </el-col>
          </el-row>
        </div>
        
        <!-- 统计数据卡片 -->
        <el-row :gutter="20" style="margin-bottom: 20px;" v-loading="loading" element-loading-text="加载中..." element-loading-background="rgba(255, 255, 255, 0.8)">
          <el-col :span="6">
            <el-card class="stat-card">
              <div class="stat-item">
                <div class="stat-label">总会员数</div>
                <div class="stat-value">{{ totalMembers }}</div>
              </div>
            </el-card>
          </el-col>
          <el-col :span="6">
            <el-card class="stat-card">
              <div class="stat-item">
                <div class="stat-label">新增会员</div>
                <div class="stat-value">{{ newMembers }}</div>
              </div>
            </el-card>
          </el-col>
          <el-col :span="6">
            <el-card class="stat-card">
              <div class="stat-item">
                <div class="stat-label">活跃会员</div>
                <div class="stat-value">{{ activeMembers }}</div>
              </div>
            </el-card>
          </el-col>
          <el-col :span="6">
            <el-card class="stat-card">
              <div class="stat-item">
                <div class="stat-label">平均等级</div>
                <div class="stat-value">{{ avgLevel.toFixed(1) }}</div>
              </div>
            </el-card>
          </el-col>
        </el-row>
        
        <!-- 图表显示区域 -->
        <div class="charts-grid">
          <!-- 注册趋势 -->
          <el-card class="chart-card full-width">
            <template #header>
              <div class="chart-title">注册趋势</div>
            </template>
            <div class="chart-container" v-loading="loading" element-loading-text="加载中..." element-loading-background="rgba(255, 255, 255, 0.8)">
              <canvas ref="registrationChart" style="width: 100%; height: 300px;"></canvas>
            </div>
          </el-card>
          
          <!-- 会员等级分布 -->
          <el-card class="chart-card full-width">
            <template #header>
              <div class="chart-title">会员等级分布</div>
            </template>
            <div class="chart-container" v-loading="loading" element-loading-text="加载中..." element-loading-background="rgba(255, 255, 255, 0.8)">
              <canvas ref="levelChart" style="width: 100%; height: 300px;"></canvas>
            </div>
          </el-card>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import { ref, onMounted, watch, nextTick } from 'vue';
import { DataLine } from '@element-plus/icons-vue';
import * as memberApi from '../../api/member';

export default {
  name: 'MemberAnalysis',
  components: {
    DataLine
  },
  setup() {
    // 图表引用
    const registrationChart = ref(null);
    const levelChart = ref(null);
    
    // 数据
    const timeRange = ref('30'); // 默认显示最近30天
    const loading = ref(false);
    
    // 统计数据
    const totalMembers = ref(0);
    const newMembers = ref(0);
    const activeMembers = ref(0);
    const avgLevel = ref(0);
    
    // 图表数据
    const favoriteData = ref([]);
    const genderData = ref([]);
    const registrationTrends = ref([]);
    const levelData = ref([]);
    
    // 生成模拟注册趋势数据
    const generateMockRegistrationTrends = () => {
      registrationTrends.value = [];
      const today = new Date();
      for (let i = parseInt(timeRange.value) - 1; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(today.getDate() - i);
        const dateStr = `${date.getMonth() + 1}/${date.getDate()}`;
        registrationTrends.value.push({
          date: dateStr,
          count: Math.floor(Math.random() * 20) + 5
        });
      }
    };
    
    // 使用模拟数据
    const useMockData = () => {
      // 统计数据
      totalMembers.value = Math.floor(Math.random() * 5000) + 1000;
      newMembers.value = Math.floor(Math.random() * 200) + 50;
      activeMembers.value = Math.floor(totalMembers.value * 0.7);
      avgLevel.value = parseFloat((Math.random() * 4 + 1).toFixed(1));
      
      // 收藏饼状图数据
      favoriteData.value = [
        { name: '景点', value: Math.floor(Math.random() * 500) + 300 },
        { name: '酒店', value: Math.floor(Math.random() * 500) + 300 },
        { name: '商品', value: Math.floor(Math.random() * 300) + 100 },
        { name: '活动', value: Math.floor(Math.random() * 200) + 50 },
        { name: '其他', value: Math.floor(Math.random() * 100) + 20 }
      ];
      
      // 会员性别分布数据
      const maleCount = Math.floor(totalMembers.value * (Math.random() * 0.3 + 0.4));
      const femaleCount = Math.floor(totalMembers.value * (Math.random() * 0.3 + 0.2));
      const unknownCount = totalMembers.value - maleCount - femaleCount;
      genderData.value = [
        { name: '男', value: maleCount },
        { name: '女', value: femaleCount },
        { name: '未知', value: unknownCount }
      ];
      
      // 注册趋势数据
      generateMockRegistrationTrends();
      
      // 会员等级分布数据
      levelData.value = [
        { name: '等级1', value: Math.floor(totalMembers.value * 0.3) },
        { name: '等级2', value: Math.floor(totalMembers.value * 0.25) },
        { name: '等级3', value: Math.floor(totalMembers.value * 0.2) },
        { name: '等级4', value: Math.floor(totalMembers.value * 0.15) },
        { name: '等级5', value: Math.floor(totalMembers.value * 0.08) },
        { name: '等级6', value: Math.floor(totalMembers.value * 0.02) }
      ];
    };
    
    // 获取会员分析数据
    const fetchData = async () => {
      try {
        // 显示loading
        loading.value = true;
        
        // 调用会员统计API获取真实数据
        const response = await memberApi.getMemberStatistics(parseInt(timeRange.value));
        if (response.code === 200 && response.data) {
          const data = response.data;
          
          // 填充统计数据
          totalMembers.value = data.total_members || 0;
          newMembers.value = data.new_members || 0;
          activeMembers.value = data.active_members || 0;
          avgLevel.value = data.avg_level || 0;
          
          // 处理收藏饼状图数据
          favoriteData.value = data.favorite_distribution || [];
          
          // 处理会员性别分布数据
          genderData.value = data.gender_distribution || [];
          
          // 处理注册趋势数据
          registrationTrends.value = (data.registration_trends || []).map(item => {
            // 转换日期格式为 MM/DD
            const dateParts = item.date.split('-');
            const dateStr = `${parseInt(dateParts[1])}/${parseInt(dateParts[2])}`;
            return {
              date: dateStr,
              count: item.count || 0
            };
          });
          
          // 处理会员等级分布数据
          levelData.value = data.level_distribution || [];
        } else {
          // 调用失败，使用模拟数据
          useMockData();
        }
        
        // 绘制图表
        nextTick(() => {
          drawRegistrationChart();
          drawLevelChart();
        });
      } catch (error) {
        console.error('获取会员分析数据失败:', error);
        // 出错时使用模拟数据
        useMockData();
        nextTick(() => {
          drawRegistrationChart();
          drawLevelChart();
        });
      } finally {
        // 隐藏loading
        loading.value = false;
      }
    };
    
    // 绘制注册趋势图
    const drawRegistrationChart = () => {
      const canvas = registrationChart.value;
      if (!canvas) return;
      
      const ctx = canvas.getContext('2d');
      const displayWidth = canvas.offsetWidth;
      const displayHeight = canvas.offsetHeight;
      
      // 获取设备像素比，解决模糊问题
      const devicePixelRatio = window.devicePixelRatio || 1;
      
      // 设置Canvas实际尺寸，考虑设备像素比
      canvas.width = displayWidth * devicePixelRatio;
      canvas.height = displayHeight * devicePixelRatio;
      
      // 设置Canvas显示尺寸
      canvas.style.width = `${displayWidth}px`;
      canvas.style.height = `${displayHeight}px`;
      
      // 缩放上下文，确保绘制清晰
      ctx.scale(devicePixelRatio, devicePixelRatio);
      
      // 清空画布
      ctx.clearRect(0, 0, displayWidth, displayHeight);
      
      // 设置边距
      const margin = { top: 20, right: 40, bottom: 60, left: 60 };
      const chartWidth = displayWidth - margin.left - margin.right;
      const chartHeight = displayHeight - margin.top - margin.bottom;
      
      // 如果没有数据，显示默认信息
      if (registrationTrends.value.length === 0) {
        // 绘制坐标轴
        ctx.strokeStyle = '#e4e7ed';
        ctx.lineWidth = 1;
        
        // X轴
        ctx.beginPath();
        ctx.moveTo(margin.left, margin.top + chartHeight);
        ctx.lineTo(margin.left + chartWidth, margin.top + chartHeight);
        ctx.stroke();
        
        // Y轴
        ctx.beginPath();
        ctx.moveTo(margin.left, margin.top);
        ctx.lineTo(margin.left, margin.top + chartHeight);
        ctx.stroke();
        
        // 显示暂无数据
        ctx.fillStyle = '#2c3e50';
        ctx.font = '16px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('暂无注册趋势数据', displayWidth / 2, displayHeight / 2);
        return;
      }
      
      // 计算数据范围
      const maxCount = Math.max(...registrationTrends.value.map(item => item.count)) || 1; // 避免maxCount为0
      
      // 计算坐标转换函数
      const xScale = (index) => margin.left + (index / (registrationTrends.value.length - 1)) * chartWidth;
      const yScale = (count) => margin.top + chartHeight - (count / maxCount) * chartHeight;
      
      // 绘制坐标轴
      ctx.strokeStyle = '#e4e7ed';
      ctx.lineWidth = 1;
      
      // X轴
      ctx.beginPath();
      ctx.moveTo(margin.left, margin.top + chartHeight);
      ctx.lineTo(margin.left + chartWidth, margin.top + chartHeight);
      ctx.stroke();
      
      // Y轴
      ctx.beginPath();
      ctx.moveTo(margin.left, margin.top);
      ctx.lineTo(margin.left, margin.top + chartHeight);
      ctx.stroke();
      
      // 绘制网格线
      ctx.strokeStyle = '#f0f2f5';
      ctx.lineWidth = 0.5;
      
      // 水平网格线（5条）
      for (let i = 0; i <= 5; i++) {
        const y = margin.top + (i / 5) * chartHeight;
        ctx.beginPath();
        ctx.moveTo(margin.left, y);
        ctx.lineTo(margin.left + chartWidth, y);
        ctx.stroke();
      }
      
      // 绘制折线图
      ctx.strokeStyle = '#667eea';
      ctx.lineWidth = 2;
      ctx.beginPath();
      
      registrationTrends.value.forEach((item, index) => {
        const x = xScale(index);
        const y = yScale(item.count);
        if (index === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });
      ctx.stroke();
      
      // 绘制数据点
      ctx.fillStyle = '#667eea';
      registrationTrends.value.forEach((item, index) => {
        const x = xScale(index);
        const y = yScale(item.count);
        ctx.beginPath();
        ctx.arc(x, y, 4, 0, 2 * Math.PI);
        ctx.fill();
      });
      
      // 绘制日期标签（每隔5天显示一个）
      ctx.fillStyle = '#606266';
      ctx.font = '12px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      registrationTrends.value.forEach((item, index) => {
        if (index % Math.ceil(registrationTrends.value.length / 10) === 0) {
          const x = xScale(index);
          const y = margin.top + chartHeight + 10;
          ctx.fillText(item.date, x, y);
        }
      });
      
      // 绘制数值标签
      ctx.fillStyle = '#606266';
      ctx.font = '12px sans-serif';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      for (let i = 0; i <= 5; i++) {
        const y = margin.top + (i / 5) * chartHeight;
        const count = Math.round(maxCount * (1 - i / 5));
        ctx.fillText(count.toString(), margin.left - 10, y);
      }
    };
    
    // 绘制会员等级分布柱状图
    const drawLevelChart = () => {
      const canvas = levelChart.value;
      if (!canvas) return;
      
      const ctx = canvas.getContext('2d');
      const displayWidth = canvas.offsetWidth;
      const displayHeight = canvas.offsetHeight;
      
      // 获取设备像素比，解决模糊问题
      const devicePixelRatio = window.devicePixelRatio || 1;
      
      // 设置Canvas实际尺寸，考虑设备像素比
      canvas.width = displayWidth * devicePixelRatio;
      canvas.height = displayHeight * devicePixelRatio;
      
      // 设置Canvas显示尺寸
      canvas.style.width = `${displayWidth}px`;
      canvas.style.height = `${displayHeight}px`;
      
      // 缩放上下文，确保绘制清晰
      ctx.scale(devicePixelRatio, devicePixelRatio);
      
      // 清空画布
      ctx.clearRect(0, 0, displayWidth, displayHeight);
      
      // 设置边距
      const margin = { top: 20, right: 40, bottom: 60, left: 60 };
      const chartWidth = displayWidth - margin.left - margin.right;
      const chartHeight = displayHeight - margin.top - margin.bottom;
      
      // 如果没有数据，显示默认信息
      if (levelData.value.length === 0) {
        // 绘制坐标轴
        ctx.strokeStyle = '#e4e7ed';
        ctx.lineWidth = 1;
        
        // X轴
        ctx.beginPath();
        ctx.moveTo(margin.left, margin.top + chartHeight);
        ctx.lineTo(margin.left + chartWidth, margin.top + chartHeight);
        ctx.stroke();
        
        // Y轴
        ctx.beginPath();
        ctx.moveTo(margin.left, margin.top);
        ctx.lineTo(margin.left, margin.top + chartHeight);
        ctx.stroke();
        
        // 显示暂无数据
        ctx.fillStyle = '#2c3e50';
        ctx.font = '16px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('暂无会员等级分布数据', displayWidth / 2, displayHeight / 2);
        return;
      }
      
      // 计算数据范围
      const maxValue = Math.max(...levelData.value.map(item => item.value)) || 1; // 避免maxValue为0
      
      // 计算柱状图宽度和间距
      const barWidth = chartWidth / levelData.value.length * 0.6;
      const barSpacing = chartWidth / levelData.value.length * 0.4;
      
      // 计算坐标转换函数
      const xScale = (index) => margin.left + index * (barWidth + barSpacing) + barSpacing / 2;
      const yScale = (value) => margin.top + chartHeight - (value / maxValue) * chartHeight;
      
      // 绘制坐标轴
      ctx.strokeStyle = '#e4e7ed';
      ctx.lineWidth = 1;
      
      // X轴
      ctx.beginPath();
      ctx.moveTo(margin.left, margin.top + chartHeight);
      ctx.lineTo(margin.left + chartWidth, margin.top + chartHeight);
      ctx.stroke();
      
      // Y轴
      ctx.beginPath();
      ctx.moveTo(margin.left, margin.top);
      ctx.lineTo(margin.left, margin.top + chartHeight);
      ctx.stroke();
      
      // 绘制网格线
      ctx.strokeStyle = '#f0f2f5';
      ctx.lineWidth = 0.5;
      
      // 水平网格线（5条）
      for (let i = 0; i <= 5; i++) {
        const y = margin.top + (i / 5) * chartHeight;
        ctx.beginPath();
        ctx.moveTo(margin.left, y);
        ctx.lineTo(margin.left + chartWidth, y);
        ctx.stroke();
      }
      
      // 颜色数组
      const colors = ['#667eea', '#764ba2', '#f093fb', '#4facfe', '#43e97b', '#fa709a'];
      
      // 绘制柱状图
      levelData.value.forEach((item, index) => {
        const x = xScale(index);
        const y = yScale(item.value);
        const barHeight = margin.top + chartHeight - y;
        
        ctx.fillStyle = colors[index % colors.length];
        ctx.fillRect(x, y, barWidth, barHeight);
        
        // 绘制柱子顶部数值
        ctx.fillStyle = '#2c3e50';
        ctx.font = '12px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText(item.value.toString(), x + barWidth / 2, y - 5);
        
        // 绘制等级标签
        ctx.fillStyle = '#606266';
        ctx.font = '12px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.fillText(item.name, x + barWidth / 2, margin.top + chartHeight + 10);
      });
      
      // 绘制Y轴数值标签
      ctx.fillStyle = '#606266';
      ctx.font = '12px sans-serif';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      for (let i = 0; i <= 5; i++) {
        const y = margin.top + (i / 5) * chartHeight;
        const value = Math.round(maxValue * (1 - i / 5));
        ctx.fillText(value.toString(), margin.left - 10, y);
      }
    };
    
    // 监听数据变化，重新绘制图表
    watch([registrationTrends, levelData], () => {
      nextTick(() => {
        drawRegistrationChart();
        drawLevelChart();
      });
    }, { deep: true });
    
    // 监听窗口大小变化，重新绘制图表
    onMounted(() => {
      // 获取数据
      fetchData();
      
      // 监听窗口大小变化
      window.addEventListener('resize', () => {
        drawRegistrationChart();
        drawLevelChart();
      });
    });
    
    return {
      timeRange,
      loading,
      totalMembers,
      newMembers,
      activeMembers,
      avgLevel,
      registrationChart,
      levelChart,
      fetchData
    };
  }
};
</script>

<style scoped>
.member-analysis {
  padding: 20px;
  background: #ffffff;
  min-height: calc(100vh - 60px);
}

.main-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  align-items: center;
  font-weight: 600;
}

.card-icon {
  font-size: 20px;
  margin-right: 8px;
  color: #667eea;
}

.card-content {
  padding: 20px 0;
}

.chart-config {
  margin-bottom: 20px;
  padding: 10px;
  background: #f5f7fa;
  border-radius: 8px;
}

.charts-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.chart-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.chart-card.full-width {
  grid-column: 1 / -1;
}

.chart-card .el-card__body {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.chart-title {
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
}

.chart-container {
  margin: 10px 0;
  padding: 10px;
  background: #f5f7fa;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.stat-card {
  height: 100%;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 0;
}

.stat-label {
  font-size: 14px;
  color: #606266;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
  color: #2c3e50;
}
</style>